import { scry, scryImageUrl } from "../lib/scryClient";
// src/routes/Dashboard.tsx
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { auth, db } from "@/lib/firebase";
import {
  collection,
  collectionGroup,
  doc,
  documentId,
  getDoc, // <-- add this
  getDocs,
  limit,
  orderBy,
  query,
  where,
  type Timestamp,
  type DocumentData,
  type QueryDocumentSnapshot,
  onSnapshot,
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { getAutoLocationOnLogin } from "@/data/locationPref";
import { Card } from "@/components/ui/card";
import { Sparkles, Star, Users, MapPin, Zap, Newspaper, ChevronLeft, ChevronRight } from "lucide-react";
import { haversineMiles } from "@/lib/geo";
import RankBadge from "@/components/RankBadge";
import PriceStrip from "@/components/PriceStrip";
import { requestFollow } from "@/data/follow";
import SectionShell from "@/components/SectionShell";
import StatTile from "@/components/StatTile";
import CardDetailsModal from "@/components/CardDetailsModal";
import LocationConfirmGate from "@/components/location/LocationConfirmGate";
import { getCurrentCoordinates } from "@/lib/userLocation";
import { saveCurrentLocationPreference, saveAutoLocationPreference } from "@/lib/userSettings";
import ProfileInlineMenu from "@/components/profile/ProfileInlineMenu";
import HeatmapWidget from "@/components/heatmap/HeatmapWidget";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { useTour } from "@/tour/TourProvider";
import { useUserStats } from "@/hooks/useUserStats";
import { useUserCollectionStats } from "@/hooks/useUserCollectionStats";
import { createPortal } from "react-dom";
import { Button } from "@/components/ui/button";
import { publishUserHeatPing } from "@/lib/publishHeatPing";
import NearbyChips from "@/events/NearbyChips";
import { sendOffer } from "@/data/offers";
import { getAvatarUrlForProfile } from "@/lib/avatars";
import ListingProfileCard from "@/components/community/ListingProfileCard";

// Toggle CTA via env (set VITE_SHOW_IGG_CTA=true to show)
function envBool(name: string, def = false): boolean {
  const v = (import.meta.env as any)[name];
  if (v == null) return def;
  return String(v).trim().toLowerCase() === "true";
}
const SHOW_IGG_CTA = envBool("VITE_SHOW_IGG_CTA", false);

// ---------------- Types ----------------
type UserProfile = {
  username?: string | null;
  display_name?: string | null;
  avatar_url?: string | null;
  bio?: string | null;
  is_profile_complete?: boolean;

  location?: {
    city?: string;
    state?: string;
    lat?: number | null;
    lng?: number | null;
  };
  play_radius?: number; // miles
  trade_radius?: number; // miles
  play_formats?: string[];
  play_formats_norm?: string[];
  preferences?: {
    available_for_trading?: boolean;
    available_for_play?: boolean;
  };

  // From rank.ts updater
  rank_title?: string | null;
  total_score?: number | null;
  rank_points_total?: number | null; // NEW
};

type UserCard = {
  id: string;
  name: string;
  image?: string;
  status: "sale" | "trade" | "keep";
  qty?: number;
  price?: number;
  rarity?: "common" | "uncommon" | "rare" | "mythic";
  createdAt?: Timestamp;
};

type ActivityCard = {
  id: string; // full path users/{uid}/cards/{cardId}
  ownerUid: string;
  ownerName: string;
  username?: string;
  ownerAvatar?: string | null;
  name: string;
  image?: string | null;
  status: "sale" | "trade";
  set?: string | null;
  number?: string | null;
  foil?: boolean;
  price?: number | null;
  scryfall_id?: string;
  createdAt?: Timestamp;
  distanceMi?: number;
  // flags so a single card can indicate both sale and trade
  for_sale?: boolean;
  for_trade?: boolean;
};

// Small labeled value row (kept as in your version)
function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-slate-300">{label}</span>
      <span className="text-white font-semibold">{value}</span>
    </div>
  );
}

// ---------------- Helpers outside component (stable identities) ----------------
// Small array chunker
function chunk<T>(arr: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

// users/{uid}/cards/{cardId} -> uid
function uidFromCardPath(path: string): string | null {
  const parts = String(path).split("/");
  const i = parts.indexOf("users");
  return i >= 0 && parts[i + 1] ? parts[i + 1] : null;
}

// users/{uid}/cards/{cardId} -> cardId
function cardIdFromPath(path: string): string | null {
  const parts = String(path).split("/");
  const i = parts.indexOf("cards");
  return i >= 0 && parts[i + 1] ? parts[i + 1] : null;
}

function scryfallImgFallback(scryId: string) {
  return ` + scryImageUrl(scryId, "normal") + `;
}

function normalizeCardName(raw: string | null | undefined): string {
  if (!raw) return "";
  const base = String(raw)
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "");
  const firstFace = base.split("//")[0];
  return firstFace.replace(/[^a-z0-9]+/g, " ").trim();
}

const FORMAT_LABELS: Record<string, string> = {
  commander: "Commander",
  cedh: "cEDH",
  standard: "Standard",
  modern: "Modern",
  pioneer: "Pioneer",
  legacy: "Legacy",
  vintage: "Vintage",
  pauper: "Pauper",
  draft: "Draft",
  sealed: "Sealed",
  "mtg arena": "MTG Arena",
  explorer: "Explorer",
  historic: "Historic",
  alchemy: "Alchemy",
  "pauper commander": "Pauper Commander",
};
const ALLOWED_FORMATS = new Set(Object.keys(FORMAT_LABELS));

function normalizeFormat(s: string): string | null {
  const raw = String(s ?? "").trim();
  if (!raw) return null;
  const x = raw.toLowerCase().replace(/\s+/g, " ");
  if (x === "edh" || x === "commander (edh)") return "commander";
  if (x === "c edh" || x === "c-edh" || x === "cedh") return "cedh";
  if (x === "sealed deck") return "sealed";
  if (x === "drafting") return "draft";
  if (x === "pauper edh" || x === "pdh" || x === "pedh") return "pauper commander";
  return ALLOWED_FORMATS.has(x) ? x : null;
}

// simple client-mounted guard to avoid StrictMode double-invoke loops for Leaflet
function useClientMounted() {
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  return mounted;
}

export default function Dashboard(): JSX.Element {
  const navigate = useNavigate();
  const loc = useLocation();
  const tour = useTour();
  const [uid, setUid] = useState<string | null>(auth.currentUser?.uid ?? null);
  const [me, setMe] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  // personal stats (from own cards)
  const [cards, setCards] = useState<UserCard[]>([]);

  // sections
  const [followedActivity, setFollowedActivity] = useState<ActivityCard[]>([]);
  const [nearbyTrade, setNearbyTrade] = useState<ActivityCard[]>([]);
  const [playOpps, setPlayOpps] = useState<
    Array<{
      uid: string;
      username: string;
      avatar_url?: string | null;
      distanceMi?: number;
      formats?: string[];
    }>
  >([]);
  // Events deck: closest 10 events, sorted soonest -> latest (and by distance)
  const [eventsNearby, setEventsNearby] = useState<Array<any>>([]);
  const [eventsIndex, setEventsIndex] = useState(0);
  // helper: convert various timestamp shapes to Date
  function toDateLike(tm: any): Date | null {
    if (!tm) return null;
    if (tm?.toDate) {
      try { return tm.toDate(); } catch { return null; }
    }
    if (tm?.toMillis) {
      try { return new Date(tm.toMillis()); } catch { return null; }
    }
    if (typeof tm === "number") {
      const d = new Date(tm);
      return isNaN(d.getTime()) ? null : d;
    }
    try {
      const d = new Date(String(tm));
      return isNaN(d.getTime()) ? null : d;
    } catch { return null; }
  }
  // Format small date string
  function fmtShortDate(tm: any) {
    const d = toDateLike(tm);
    if (!d) return "TBD";
    return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "numeric" });
  }

  const [menu, setMenu] = useState<{
    key: string; // ownerUid:docPath
    username: string;
    ownerUid: string;
  } | null>(null);

  // Offer modal state (matches Marketplace flow)
  const [offerOpen, setOfferOpen] = useState(false);
  const [offerCard, setOfferCard] = useState<ActivityCard | null>(null);
  const [offerMessage, setOfferMessage] = useState("");
  const [offerPrice, setOfferPrice] = useState<string>("");
  const [submittingOffer, setSubmittingOffer] = useState(false);

  // Floating inline menu anchor and viewport-box (prevents clipping)
  const listingAnchorRef = React.useRef<HTMLElement | null>(null);
  const [listingMenuBox, setListingMenuBox] = useState<{ top: number; left: number; width: number } | null>(null);
  const MENU_MAX_HEIGHT = 420; // px, conservative max for the menu’s height

  // zoom modal (oracle details)
  const [zoom, setZoom] = useState<ActivityCard | null>(null);

  // MISSING UI/state restored
  const mounted = useClientMounted();
  const [autoLocationOnLogin, setAutoLocationOnLoginState] = useState<boolean>(false);
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [locBusy, setLocBusy] = useState(false);
  const [missingLocNagOpen, setMissingLocNagOpen] = useState(false);
  const [newsItems, setNewsItems] = useState<string[]>([]);
  const [newsUpdatedAt, setNewsUpdatedAt] = useState<number | null>(null);
  const [newsErr, setNewsErr] = useState<string | null>(null);
  const [alerts, setAlerts] = useState<ActivityCard[]>([]);
  const [alertsPending, setAlertsPending] = useState(false);
  const [expandFollowed, setExpandFollowed] = useState(false);
  const [expandNearby, setExpandNearby] = useState(false);
  const COLLAPSE_COUNT = 5;

  // Offer flow (mirror Marketplace)
  function openOffer(it: ActivityCard) {
    setOfferCard(it);
    setOfferMessage("");
    setOfferPrice("");
    setOfferOpen(true);
  }
  function openOfferFromZoom() {
    if (zoom) openOffer(zoom);
  }
  async function submitOffer() {
    if (!offerCard) return;
    setSubmittingOffer(true);
    try {
      const parsed =
        offerCard.for_sale && offerPrice
          ? Number(offerPrice)
          : null;
      if (offerCard.for_sale && offerPrice && !Number.isFinite(parsed)) {
        console.warn("Invalid price");
        return;
      }
      await sendOffer({
        toUid: offerCard.ownerUid,
        cardId: cardIdFromPath(offerCard.id) ?? undefined,
        cardOwnerUid: offerCard.ownerUid,
        cardName: offerCard.name,
        scryfall_id: offerCard.scryfall_id,
        offerType: offerCard.for_sale ? "purchase" : "trade",
        price: parsed,
        message: offerMessage || null,
      });
      setOfferOpen(false);
    } catch (e) {
      console.error(e);
    } finally {
      setSubmittingOffer(false);
    }
  }

  // Open card details modal for the currently selected listing menu target
  function openCardDetailsByMenu() {
    if (!menu) return;
    // Nearby Players has no card context -> go to DM
    if (menu.key.endsWith(":nearby")) {
      navigate(`/inbox?to=${encodeURIComponent(menu.ownerUid)}`);
      return;
    }
    const all = [...followedActivity, ...nearbyTrade, ...alerts];
    const found = all.find((x) => `${x.ownerUid}:${x.id}` === menu.key);
    if (found) setZoom(found);
  }

  // Inline seller menu toggler (sets the anchor for portal positioning)
  function toggleMenuListing(
    ownerUid: string,
    username: string,
    docKey: string,
    anchorEl?: HTMLElement,
  ) {
    const key = `${ownerUid}:${docKey}`;
    setMenu((m) => {
      if (m && m.key === key) {
        listingAnchorRef.current = null;
        setListingMenuBox(null);
        return null;
      }
      if (anchorEl) {
        listingAnchorRef.current = anchorEl;
      } else {
        const el = document.querySelector<HTMLElement>(`[data-listing-key="${key}"]`);
        listingAnchorRef.current = el || null;
      }
      return { key, username, ownerUid };
    });
  }

  // Compute floating menu position and keep it visible in viewport
  useEffect(() => {
    if (!menu) {
      setListingMenuBox(null);
      return;
    }
    const compute = () => {
      const el =
        listingAnchorRef.current ||
        document.querySelector<HTMLElement>(`[data-listing-key="${menu.key}"]`);
      if (!el) {
        setListingMenuBox(null);
        return;
      }
      const r = el.getBoundingClientRect();
      const top = Math.min(
        Math.max(8, r.top - 8),
        Math.max(8, window.innerHeight - MENU_MAX_HEIGHT - 8),
      );
      const left = Math.min(Math.max(8, r.left), window.innerWidth - 340 - 8);
      setListingMenuBox({ top, left, width: r.width });
    };
    compute();
    window.addEventListener("scroll", compute, true);
    window.addEventListener("resize", compute);
    return () => {
      window.removeEventListener("scroll", compute, true);
      window.removeEventListener("resize", compute);
    };
  }, [menu]);

  // Simple profile nav used by inline menu
  function viewProfile(username?: string) {
    if (!username) return;
    const handle = username.replace(/^@+/, "");
    navigate(`/${encodeURIComponent(handle)}`);
  }

 // ---------------- Hooks ----------------
  // Server-backed counters (exact) — call before any conditional returns
  const statsAgg = useUserCollectionStats(uid);
  // Optional: keep legacy stats doc as secondary source if you want
  // const statsDoc = useUserStats(uid);

  // Fallback counts from local slice
  const saleCountEff = statsAgg?.sale ?? 0;
  const tradeCountEff = statsAgg?.trade ?? 0;

  // Load saved preference after login
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const v = await getAutoLocationOnLogin();
        if (alive) setAutoLocationOnLoginState(v);
      } catch {
        if (alive) setAutoLocationOnLoginState(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // keep uid up-to-date
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => setUid(u?.uid ?? null));
    return () => unsub();
  }, []);

  // load current pref (dedup to same state)
  useEffect(() => {
    const u = auth.currentUser;
    if (!u) return;
    (async () => {
      try {
        const snap = await getDoc(doc(db, "users", u.uid));
        const v = (snap.exists() ? snap.data() : {}) as any;
        setAutoLocationOnLoginState(Boolean(v?.locationPreference?.autoOnLogin));
      } catch {}
    })();
  }, []);

  // show modal or auto-apply current location after sign-in, once per session
  useEffect(() => {
    const u = auth.currentUser;
    if (!u) return;
    if (sessionStorage.getItem("sg_loc_confirm_shown") === "1") return;

    if (autoLocationOnLogin) {
      (async () => {
        try {
          await applyCurrentLocation();
        } finally {
          sessionStorage.setItem("sg_loc_confirm_shown", "1");
        }
      })();
    } else {
      const t = setTimeout(() => setLocationModalOpen(true), 0);
      sessionStorage.setItem("sg_loc_confirm_shown", "1");
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoLocationOnLogin]);

  async function applyCurrentLocation() {
    const uid = auth.currentUser?.uid;
    if (!uid || locBusy) return;
    setLocBusy(true);
    try {
      const coords = await getCurrentCoordinates();
      await saveCurrentLocationPreference(uid, coords);
      setLocationModalOpen(false);
    } catch (e) {
      console.warn("Location update failed:", e);
      setLocationModalOpen(false);
    } finally {
      setLocBusy(false);
    }
  }

  async function toggleAutoLocation(next: boolean) {
    const uid = auth.currentUser?.uid;
    if (!uid) return;
    try {
      setAutoLocationOnLoginState(next);
      await saveAutoLocationPreference(uid, next);
    } catch (e) {
      setAutoLocationOnLoginState((v) => !v);
      console.warn("Failed to save auto location pref:", e);
    }
  }

  // What’s New (news)
  useEffect(() => {
    setNewsErr(null);
    const ref = doc(db, "scoreboard_public", "news");
    const stop = onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists()) {
          setNewsItems([]);
          setNewsUpdatedAt(null);
          return;
        }
        const v = snap.data() as any;
        const list: string[] = [];
        if (Array.isArray(v?.latest)) list.push(...v.latest);
        if (typeof v?.latestText === "string" && v.latestText.trim()) {
          list.unshift(v.latestText);
        }
        // Clean, dedupe, and cap
        const clean = list
          .map((s) => String(s).replace(/^📰\s*/i, "").trim())
          .filter(Boolean);
        const seen = new Set<string>();
        const out: string[] = [];
        for (const s of clean) {
          const key = s.toLowerCase();
          if (seen.has(key)) continue;
          seen.add(key);
          out.push(s);
          if (out.length >= 10) break;
        }
        setNewsItems(out);
        const ms =
          typeof v?.updatedAt?.toMillis === "function"
            ? v.updatedAt.toMillis()
            : typeof v?.updatedAt === "number"
            ? v.updatedAt
            : null;
        setNewsUpdatedAt(ms);
      },
      (err) => {
        setNewsErr(err?.message || "Failed to load news.");
        setNewsItems([]);
        setNewsUpdatedAt(null);
      }
    );
    return () => stop();
  }, []);

  // Load profile + own recent cards (for stats)
  useEffect(() => {
    (async () => {
      if (!uid) {
        setMe(null);
        setCards([]);
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        // profile
        const uref = doc(db, "users", uid);
        const usnap = await getDoc(uref);
        const profile =
          (usnap.exists() ? (usnap.data() as UserProfile) : null) || null;
        setMe(profile);

        // own cards slice
        const cref = collection(db, "users", uid, "cards");
        const snap = await getDocs(
          query(cref, orderBy("createdAt", "desc"), limit(48))
        );
        const rows: UserCard[] = snap.docs.map((d) => {
          const v = d.data() as any;
          return {
            id: d.id,
            name: v.name || "Unknown",
            image: v.image,
            status: (v.status as UserCard["status"]) || "keep",
            qty: v.qty ?? 1,
            price: typeof v.price === "number" ? v.price : undefined,
            rarity: v.rarity,
            createdAt: v.createdAt,
          };
        });
        setCards(rows);
      } finally {
        setLoading(false);
      }
    })();
  }, [uid]);

  // Open a gentle nag each time Dashboard mounts if coords are not set
  useEffect(() => {
    if (!loading && me && (me.location?.lat == null || me.location?.lng == null)) {
      setMissingLocNagOpen(true);
    }
  }, [loading, me?.location?.lat, me?.location?.lng]);

  // Derived stats
  const totalCards = useMemo(
    () => cards.reduce((sum, c) => sum + (c.qty || 1), 0),
    [cards]
  );
  const forTrade = useMemo(
    () => cards.filter((c) => c.status === "trade").length,
    [cards]
  );
  const forSale = useMemo(
    () => cards.filter((c) => c.status === "sale").length,
    [cards]
  );
  const totalValue = useMemo(() => {
    return cards
      .filter((c) => c.status === "sale" && typeof c.price === "number")
      .reduce((sum, c) => sum + (c.price || 0) * (c.qty || 1), 0);
  }, [cards]);

  const mostValuable = useMemo(() => {
    const withValue = cards.filter(
      (c) => typeof c.price === "number" && c.status === "sale"
    );
    if (!withValue.length) return null;
    const top = [...withValue].sort(
      (a, b) => (b.price || 0) - (a.price || 0)
    )[0];
    return `${top.name} ($${(top.price || 0).toFixed(2)})`;
  }, [cards]);

  const newest = useMemo(() => {
    if (!cards.length) return null;
    const top = [...cards].sort(
      (a, b) =>
        (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0)
    )[0];
    return top?.name ?? null;
  }, [cards]);

  const rarest = useMemo(() => {
    const order = { mythic: 4, rare: 3, uncommon: 2, common: 1 } as const;
    const withRarity = cards.filter((c) => c.rarity);
    if (!withRarity.length) return null;
    const top = [...withRarity].sort(
      (a, b) =>
        (order[b.rarity || "common"] || 0) - (order[a.rarity || "common"] || 0)
    )[0];
    return top?.name ? `${top.name} (${top.rarity})` : null;
  }, [cards]);

  const myLat = me?.location?.lat ?? null;
  const myLng = me?.location?.lng ?? null;

  // Fallback to browser coords if profile coords are missing
  const [geoFallback, setGeoFallback] = useState<{ lat: number; lng: number } | null>(null);
  useEffect(() => {
    if (myLat != null && myLng != null) return;
    let cancelled = false;
    (async () => {
      try {
        const g = await getCurrentCoordinates();
        if (!cancelled) setGeoFallback(g);
      } catch {}
    })();
    return () => { cancelled = true; };
  }, [myLat, myLng]);

  // Followed users’ activity
  useEffect(() => {
    if (!uid) {
      setFollowedActivity([]);
      return;
    }
    (async () => {
      // who I follow
      const follows = await getDocs(
        query(
          collection(db, "follows"),
          where("followerId", "==", uid),
          where("status", "==", "approved"),
        )
      );
      const followingIds = Array.from(
        new Set(follows.docs.map((d) => (d.data() as any).followingId)),
      ).slice(0, 25);
      if (!followingIds.length) {
        setFollowedActivity([]);
        return;
      }

      // profiles map for names + avatar
      const chunks = chunk(followingIds, 10);
      const profiles = new Map<
        string,
        { username: string; avatar_url: string | null }
      >();
      for (const group of chunks) {
        const snap = await getDocs(
          query(collection(db, "users"), where(documentId(), "in", group)),
        );
        snap.forEach((u) => {
          const v = u.data() as any;
          profiles.set(u.id, {
            username: v.username || v.display_name || u.id.slice(0, 6),
            avatar_url: v.avatar_url || null,
          });
        });
      }

      // fetch recent sale/trade per followed user, then merge per card
      const perUser = 3;
      const rows: ActivityCard[] = [];
      for (const fuid of followingIds) {
        const base = collection(db, "users", fuid, "cards");
        const [saleSnap, tradeSnap] = await Promise.all([
          getDocs(
            query(
              base,
              where("for_sale", "==", true),
              orderBy("createdAt", "desc"),
              limit(perUser),
            ),
          ),
          getDocs(
            query(
              base,
              where("for_trade", "==", true),
              orderBy("createdAt", "desc"),
              limit(perUser),
            ),
          ),
        ]);

        // Combine by document path so a single card can reflect both sale + trade
        const byId = new Map<string, ActivityCard>();

        const upsert = (
          d: (typeof saleSnap.docs)[number],
          flag: "sale" | "trade",
        ) => {
          const v = d.data() as any;
          const id = d.ref.path;
          const existing = byId.get(id);
          const baseCard: ActivityCard = existing ?? {
            id,
            ownerUid: fuid,
            ownerName: profiles.get(fuid)?.username || fuid,
            username: profiles.get(fuid)?.username || fuid,
            ownerAvatar: profiles.get(fuid)?.avatar_url ?? null,
            name: v.name || "Card",
            image: (v.custom_image_url ?? v.image ?? null) || null,
            status: flag === "sale" ? "sale" : "trade",
            set: v.set ?? null,
            number: v.number ?? null,
            foil: Boolean(v.foil),
            price: typeof v.price === "number" ? v.price : null,
            scryfall_id: v.scryfall_id,
            createdAt: v.createdAt,
            for_sale: false,
            for_trade: false,
          };
          if (flag === "sale") {
            baseCard.for_sale = true;
            baseCard.price =
              typeof v.price === "number" ? v.price : (baseCard.price ?? null);
          } else {
            baseCard.for_trade = true;
          }
          baseCard.status = baseCard.for_sale ? "sale" : "trade";
          byId.set(id, baseCard);
        };

        saleSnap.forEach((d) => upsert(d, "sale"));
        tradeSnap.forEach((d) => upsert(d, "trade"));

        rows.push(...byId.values());
      }

      // Dedupe across all followed rows and keep latest createdAt
      const merged = new Map<string, ActivityCard>();
      for (const r of rows) {
        const k = `${r.ownerUid}:${r.id}`;
        const prev = merged.get(k);
        if (
          !prev ||
          (r.createdAt?.toMillis?.() ?? 0) > (prev.createdAt?.toMillis?.() ?? 0)
        ) {
          merged.set(k, r);
        }
      }
      const finalRows = Array.from(merged.values());
      finalRows.sort(
        (a, b) =>
          (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0),
      );

      setFollowedActivity(finalRows.slice(0, 36));
    })().catch(console.error);
  }, [uid]);

  // Nearby trade activity
  useEffect(() => {
    (async () => {
      // Fetch all marketplace-listed cards (sale or trade), newest first
      const qSale = query(
        collectionGroup(db, "cards"),
        where("for_sale", "==", true),
        orderBy("createdAt", "desc"),
        limit(300),
      );
      const qTrade = query(
        collectionGroup(db, "cards"),
        where("for_trade", "==", true),
        orderBy("createdAt", "desc"),
        limit(300),
      );
      const [saleSnap, tradeSnap] = await Promise.all([getDocs(qSale), getDocs(qTrade)]);

      // Cache owners and compute distance only if we have my location
      const hasMyLoc = myLat != null && myLng != null;
      const radiusMi = Math.max(Number(me?.trade_radius ?? 25), 1);
      const ownerMap = new Map<string, UserProfile>();

      // Merge and dedupe by full path so a single card can represent both sale and trade
      const byId = new Map<string, ActivityCard>();
      const upsert = async (d: QueryDocumentSnapshot<DocumentData>, flag: "sale" | "trade") => {
        const ownerUid = uidFromCardPath(d.ref.path);
        if (!ownerUid || ownerUid === uid) return;
        // minimal owner profile
        let owner = ownerMap.get(ownerUid);
        if (!owner) {
          const u = await getDoc(doc(db, "users", ownerUid));
          owner = ((u.exists() ? (u.data() as UserProfile) : null) as UserProfile) || {};
          ownerMap.set(ownerUid, owner);
        }
        const v = d.data() as any;
        const existing = byId.get(d.ref.path);
        const base: ActivityCard =
          existing ?? {
            id: d.ref.path,
            ownerUid,
            ownerName: owner.username || ownerUid.slice(0, 6),
            username: owner.username || ownerUid.slice(0, 6),
            ownerAvatar: owner.avatar_url || null,
            name: v.name || "Card",
            image: (v.custom_image_url ?? v.image ?? null) || null,
            status: flag,
            set: v.set ?? null,
            number: v.number ?? null,
            foil: Boolean(v.foil),
            price: typeof v.price === "number" ? v.price : null,
            scryfall_id: v.scryfall_id,
            createdAt: v.createdAt,
            distanceMi: undefined,
            for_sale: false,
            for_trade: false,
          };
        if (flag === "sale") {
          base.for_sale = true;
          if (typeof v.price === "number") base.price = v.price;
        } else {
          base.for_trade = true;
        }
        base.status = base.for_sale ? "sale" : "trade";

        // Distance filter only if we have my location and the owner's location
        if (hasMyLoc) {
          const oLat = owner.location?.lat ?? null;
          const oLng = owner.location?.lng ?? null;
          if (oLat != null && oLng != null) {
            base.distanceMi = haversineMiles(myLat!, myLng!, oLat, oLng);
          }
        }
        byId.set(d.ref.path, base);
      };

      for (const d of saleSnap.docs) await upsert(d, "sale");
      for (const d of tradeSnap.docs) await upsert(d, "trade");

      // Build list; apply radius filter only if we have a distance
      let out = Array.from(byId.values());
      if (hasMyLoc) {
        out = out.filter((c) => c.distanceMi != null && Number.isFinite(c.distanceMi) && (c.distanceMi as number) <= radiusMi);
      }

      // Sort newest first
      out.sort((a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0));
      setNearbyTrade(out.slice(0, 36));
    })().catch(console.error);
  }, [uid, myLat, myLng, me?.trade_radius]);

  // Load nearby public events (compute distance and pick top 10 closest then soonest)
  useEffect(() => {
    (async () => {
      try {
        // Primary: collectionGroup with ordering (preferred)
        let snap;
        try {
          const q = query(
            collectionGroup(db, "events"),
            where("visibility", "==", "public"),
            orderBy("startTime", "asc"),
            limit(500),
          );
          snap = await getDocs(q);
        } catch (err: any) {
          const code = String(err?.code || "").toLowerCase();
          console.debug("[Dashboard] events primary query failed:", code, err?.message);
          // Permission issue or collectionGroup blocked — try root collection fallback (no orderBy)
          if (code.includes("permission") || code.includes("insufficient")) {
            try {
              const q2 = query(
                collection(db, "events"),
                where("visibility", "==", "public"),
                limit(500),
              );
              snap = await getDocs(q2);
            } catch (err2: any) {
              console.warn("[Dashboard] events root collection fallback failed:", err2?.message || err2);
              throw err2;
            }
          } else if (code.includes("failed-precondition") || code.includes("index") || code.includes("missing")) {
            // Missing index for ordered collectionGroup — try the collectionGroup without the orderBy and sort client-side
            try {
              const q3 = query(
                collectionGroup(db, "events"),
                where("visibility", "==", "public"),
                limit(500),
              );
              snap = await getDocs(q3);
            } catch (err3: any) {
              console.warn("[Dashboard] events collectionGroup fallback failed:", err3?.message || err3);
              throw err3;
            }
          } else {
            throw err;
          }
        }
         const radiusMi = Math.max(Number(me?.play_radius ?? me?.trade_radius ?? 50), 1);
         const myLatVal = myLat ?? geoFallback?.lat ?? null;
         const myLngVal = myLng ?? geoFallback?.lng ?? null;
 
         const arr: any[] = [];
        for (const d of (snap?.docs || [])) {
           const v = d.data() as any;
           const loc = v.location || null;
           let distance: number | null = null;
           if (myLatVal != null && myLngVal != null && loc?.lat != null && loc?.lng != null) {
             distance = haversineMiles(myLatVal, myLngVal, loc.lat, loc.lng);
           }
           // If we have a location, filter by radius; otherwise include (unknown)
           if (distance != null && distance > radiusMi) continue;
           arr.push({
             id: d.id,
             refPath: d.ref.path,
             title: v.title || v.name || "Event",
             startTime: v.startTime,
             images: Array.isArray(v.images) ? v.images : v.image ? [v.image] : [],
             organizerUsername: v.organizerUsername || v.organizer || (v.organizerUid ? v.organizerUid : ""),
             organizerDisplayName: v.organizerDisplayName || v.organizerName || null,
             organizerAvatarUrl: v.organizerAvatarUrl || null,
             address: v.address || null,
             distanceMi: distance,
             raw: v,
           });
         }
-        // Sort by distance (undefined last) then by startTime (soonest first)
+        // Sort by distance (undefined last) then by startTime (soonest first)
         arr.sort((a, b) => {
           const da = a.distanceMi == null ? Infinity : a.distanceMi;
           const db_ = b.distanceMi == null ? Infinity : b.distanceMi;
           if (da !== db_) return da - db_;
           const ta = toDateLike(a.startTime)?.getTime() ?? 1e15;
           const tb = toDateLike(b.startTime)?.getTime() ?? 1e15;
           return ta - tb;
         });
         const top = arr.slice(0, 10);
         setEventsNearby(top);
         setEventsIndex(0);
       } catch (e) {
-        console.warn("[Dashboard] load events failed", e);
+        console.warn("[Dashboard] load events failed", e);
         setEventsNearby([]);
       }
     })();
   }, [uid, myLat, myLng, geoFallback?.lat, geoFallback?.lng, me?.play_radius, me?.trade_radius]);

  // Deduplicate nearby players to one instance per user (keep the first/closest)
  const uniqueNearbyPlayers = useMemo(() => {
    const seen = new Set<string>();
    const out: ActivityCard[] = [];
    for (const c of nearbyTrade) {
      if (seen.has(c.ownerUid)) continue;
      seen.add(c.ownerUid);
      out.push(c);
    }
    return out;
  }, [nearbyTrade]);

  // Play opportunities (normalized formats + full coverage)
  useEffect(() => {
    if (!uid) {
      setPlayOpps([]);
      return;
    }
    (async () => {
      // Prefer normalized formats for you; fall back to raw or legacy field
      const myFormatsSrc =
        (me?.play_formats_norm && me.play_formats_norm.length
          ? me.play_formats_norm
          : me?.play_formats) ||
        (me as any)?.preferences?.play_formats ||
        [];
      const myFormats = (myFormatsSrc as string[])
        .map(normalizeFormat)
        .filter((v): v is string => !!v);

      const mySet = new Set(myFormats);
      if (mySet.size === 0) {
        setPlayOpps([]);
        return;
      }

      // Require my location and radius to compute proximity
      const myLatVal = me?.location?.lat ?? null;
      const myLngVal = me?.location?.lng ?? null;
      if (myLatVal == null || myLngVal == null) {
        setPlayOpps([]);
        return;
      }
      const radiusMi = Math.max(Number(me?.play_radius ?? 25), 1);

      // Rather than a single array-contains-any with a small limit,
      // query per-format and union so we don't silently miss matches.
      const normList = Array.from(mySet);
      const seen = new Set<string>();
      const out: Array<{
        uid: string;
        username: string;
        avatar_url?: string | null;
        distanceMi?: number;
        formats?: string[];
      }> = [];

      // helper to process a batch of user docs
      const processDocs = (docs: QueryDocumentSnapshot<DocumentData>[]) => {
        for (const d of docs) {
          const uid2 = d.id;
          if (uid2 === uid || seen.has(uid2)) continue;
          const u = d.data() as UserProfile;

          const theirSrc =
            (u.play_formats_norm && u.play_formats_norm.length
              ? u.play_formats_norm
              : u.play_formats) ||
            (u as any)?.preferences?.play_formats ||
            [];
          const their = (theirSrc as string[])
            .map(normalizeFormat)
            .filter((v): v is string => !!v);
          if (their.length === 0) continue;

          // overlap?
          let overlap = false;
          for (const f of their) {
            if (mySet.has(f)) {
              overlap = true;
              break;
            }
          }
          if (!overlap) continue;

          // distance filter
          const lat = u.location?.lat ?? null;
          const lng = u.location?.lng ?? null;
          if (lat == null || lng == null) continue;
          const distanceMi = haversineMiles(myLatVal!, myLngVal!, lat, lng);
          if (!Number.isFinite(distanceMi) || distanceMi > radiusMi) continue;

          seen.add(uid2);
          out.push({
            uid: uid2,
            username: u.username || uid2.slice(0, 6),
            avatar_url: u.avatar_url || null,
            distanceMi,
            formats: their,
          });
        }
      };

      // Primary: per-format passes (larger limits to increase coverage)
      for (const f of normList.slice(0, 10)) {
        const snap = await getDocs(
          query(
            collection(db, "users"),
            where("preferences.available_for_play", "==", true),
            where("play_formats_norm", "array-contains", f),
            limit(500),
          ),
        );
        processDocs(snap.docs);
      }

      // Fallback: older profiles without normalized formats
      if (out.length === 0) {
        const snap = await getDocs(
          query(
            collection(db, "users"),
            where("preferences.available_for_play", "==", true),
            limit(1000),
          ),
        );
        processDocs(snap.docs);
      }

      // sort: closest, then username
      out.sort(
        (a, b) =>
          (a.distanceMi ?? 1e9) - (b.distanceMi ?? 1e9) ||
          a.username.localeCompare(b.username),
      );
      setPlayOpps(out); // show all matches (container scrolls)
    })().catch(console.error);
  }, [
    uid,
    me?.play_formats_norm,
    me?.play_formats,
    (me as any)?.preferences?.play_formats,
    me?.location?.lat,
    me?.location?.lng,
    me?.play_radius,
  ]);

  // Alerts from wantlist (STRICT name match with normalization)
  useEffect(() => {
    if (!uid) {
      setAlerts([]);
      return;
    }
    (async () => {
      setAlertsPending(true);
      try {
        // Load wantlist (try 'wishlist' first, then 'wants')
        const wlRef = collection(db, "users", uid, "wishlist");
        const wlSnap = await getDocs(query(wlRef, limit(500)));
        let wishDocs = wlSnap.docs;

        if (wishDocs.length === 0) {
          const wantsRef = collection(db, "users", uid, "wants");
          const wantsSnap = await getDocs(query(wantsRef, limit(500)));
          wishDocs = wantsSnap.docs;
        }

        if (!wishDocs.length) {
          setAlerts([]);
          return;
        }

        // Build normalized name set (wishlist names only)
        const wantNames = new Set<string>();
        for (const d of wishDocs) {
          const v = d.data() as any;
          const name = v?.name ?? v?.cardName ?? v?.card_name ?? null;
          const norm = normalizeCardName(name);
          if (norm) wantNames.add(norm);
        }
        if (wantNames.size === 0) {
          setAlerts([]);
          return;
        }

        // Fetch ONLY active marketplace listings (for_sale or for_trade)
        const qSale = query(
          collectionGroup(db, "cards"),
          where("for_sale", "==", true),
          orderBy("createdAt", "desc"),
          limit(200),
        );
        const qTrade = query(
          collectionGroup(db, "cards"),
          where("for_trade", "==", true),
          orderBy("createdAt", "desc"),
          limit(200),
        );

        const results = await Promise.all([getDocs(qSale), getDocs(qTrade)]);
        const ownerCache = new Map<string, UserProfile>();
        const dedup = new Map<string, ActivityCard>();

        for (const snap of results) {
          for (const d of snap.docs) {
            const v = d.data() as any;

            // PERMANENT FILTER: name must match wishlist (normalized)
            const normName = normalizeCardName(v?.name ?? null);
            if (!normName || !wantNames.has(normName)) continue;

            const ownerUid = uidFromCardPath(d.ref.path);
            // Exclude my own listings; only show other users' matches
            if (!ownerUid || ownerUid === uid) continue;

            // Minimal seller profile (for username/avatar)
            let prof = ownerCache.get(ownerUid);
            if (!prof) {
              const u = await getDoc(doc(db, "users", ownerUid));
              prof = (u.exists() ? (u.data() as UserProfile) : null) || {};
              ownerCache.set(ownerUid, prof);
            }

            const key = `${ownerUid}:${d.ref.path}`;
            const card: ActivityCard = {
              id: d.ref.path,
              ownerUid,
              ownerName: prof.username || ownerUid.slice(0, 6),
              username: prof.username || ownerUid.slice(0, 6),
              ownerAvatar: prof.avatar_url || null,
              name: v?.name || "Card",
              image: (v.custom_image_url ?? v.image ?? null) || null,
              status: v.for_sale === true ? "sale" : "trade",
              set: v.set ?? null,
              number: v.number ?? null,
              foil: Boolean(v.foil),
              price: typeof v.price === "number" ? v.price : null,
              scryfall_id: v.scryfall_id,
              createdAt: v.createdAt,
              // distance intentionally omitted from filter; alerts are name-only matches
              distanceMi: undefined,
              for_sale: Boolean(v.for_sale === true),
              for_trade: Boolean(v.for_trade === true),
            };

            const prev = dedup.get(key);
            if (
              !prev ||
              (card.createdAt?.toMillis?.() ?? 0) >
                (prev.createdAt?.toMillis?.() ?? 0)
            ) {
              dedup.set(key, card);
            }
          }
        }

        const final = Array.from(dedup.values());
        // Sort newest first (name-only matches)
        final.sort(
          (a, b) =>
            (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0),
        );

        setAlerts(final.slice(0, 50));
      } catch {
        setAlerts([]);
      } finally {
        setAlertsPending(false);
      }
    })();
  }, [uid]);

  // What’s New — launch tour if requested
  useEffect(() => {
    let should = false;
    if ((loc.state as any)?.startTour) should = true;
    try {
      if (localStorage.getItem("sg:pendingTour") === "1") {
        should = true;
        localStorage.removeItem("sg:pendingTour");
      }
    } catch {}
    if (should) {
      // Try common method names safely
      (tour as any)?.start?.();
      (tour as any)?.startTour?.();
      (tour as any)?.begin?.();
    }
  }, [loc.state, tour]);

  // --- Listing renderer (kept identical visually/behaviorally) ---
  function renderListing(it: ActivityCard) {
    const key = `${it.ownerUid}:${it.id}`;
    const isOpen = !!menu && menu.key === key;
    const distanceLabel =
      it.distanceMi != null && Number.isFinite(it.distanceMi)
        ? `${it.distanceMi.toFixed(1)} mi`
        : "—";

    return (
      <article
        key={key}
        data-listing-key={key}
        className="group relative rounded border border-gray-800 bg-black/30 hover:border-amber-400/60 transition-all duration-200 hover:-translate-y-1 hover:shadow-xl hover:shadow-amber-400/10 focus-within:-translate-y-1 focus-within:shadow-xl overflow-hidden"
        title={it.name}
      >
        <div
          className="block w-full cursor-pointer"
          role="button"
          tabIndex={0}
          onClick={() => setZoom(it)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setZoom(it);
            }
          }}
          aria-label={`Details for ${it.name}`}
        >
          <div className="relative aspect-[3/4] bg-black flex items-center justify-center overflow-hidden">
            <img
              src={
                it.image ||
                (it.scryfall_id
                  ? scryfallImgFallback(it.scryfall_id)
                  : "/images/sg-card-back.png")
              }
              alt={it.name}
              className="h-full w-full object-cover transition-transform duration-200 ease-out group-hover:scale-105 group-focus-within:scale-105"
              loading="lazy"
            />
            <span className="md:hidden absolute bottom-1 right-1 z-10 px-2 py-1 rounded bg-black/70 text-white text-[11px] border border-white/20">
              Details
            </span>

            {/* Slide-up overlay like Marketplace */}
            <div
              className="pointer-events-none absolute inset-x-0 bottom-0 translate-y-full group-hover:translate-y-0 group-focus-within:translate-y-0 transition-transform duration-200 ease-out"
              style={{ maxHeight: "80%" }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="pointer-events-auto bg-gradient-to-t from-black/85 via-black/60 to-black/10 backdrop-blur-sm border-top border-white/10 p-2 space-y-1 border-t border-white/10 max-h-full overflow-y-auto">
                <div className="text-xs font-semibold text-white truncate">{it.name}</div>
                <div className="text-[11px] opacity-80 truncate">
                  {(it.set || "")?.toUpperCase?.() || ""}
                  {it.number ? ` • #${it.number}` : ""}
                  {it.foil ? " • Foil" : ""}
                </div>

                <div className="pt-1">
                  <div className="flex items-center gap-2">
                    <button
                      className="shrink-0 rounded-full border border-gray-700 overflow-hidden"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleMenuListing(it.ownerUid, it.username || it.ownerName, it.id, e.currentTarget as HTMLElement);
                      }}
                      aria-label={`Seller ${it.username || it.ownerName}`}
                    >
                      <img
                        src={it.ownerAvatar || "/images/avatars/default.png"}
                        alt={it.username || it.ownerName}
                        className="h-5 w-5 object-cover"
                      />
                    </button>
                    <button
                      className="text-[11px] underline truncate"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleMenuListing(it.ownerUid, it.username || it.ownerName, it.id, e.currentTarget as HTMLElement);
                      }}
                      title={`@${it.username || it.ownerName}`}
                    >
                      @{it.username || it.ownerName}
                    </button>

                    <div className="ml-auto text-[11px] opacity-70">{distanceLabel}</div>
                  </div>

                  {/* Menu rendered via floating portal (prevents clipping) */}
                  {isOpen && <div className="hidden" aria-hidden="true" />}
                </div>

                {it.scryfall_id ? <PriceStrip scryfallId={it.scryfall_id} compact /> : null}
                <div className="flex items-center justify-between pt-1">
                  <span className="text-xs font-semibold text-amber-300">
                    {it.for_sale && it.for_trade
                      ? `Trade • ${it.price != null ? `$${it.price.toFixed(2)}` : "—"}`
                      : it.for_sale
                      ? it.price != null
                        ? `$${it.price.toFixed(2)}`
                        : "—"
                      : "Trade"}
                  </span>
                  <button
                    type="button"
                    className="px-2 py-1 rounded border border-amber-700 text-amber-200 hover:bg-amber-900/20 text-xs"
                    onClick={(e) => {
                      e.stopPropagation();
                      setZoom(it); // open Card Details first, user then clicks Make offer in modal
                    }}
                  >
                    Make offer
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </article>
    );
  }

  // Early returns
  if (!uid && !loading) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <Card className="glass-effect border-slate-700/50 p-6">
          <h2 className="text-white text-xl font-semibold mb-2">
            You’re signed out
          </h2>
          <p className="text-slate-300 mb-4">
            Please sign in to view your dashboard.
          </p>
          <Link to="/" className="btn-pixel btn-pixel-primary">
            Go to Home
          </Link>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  // -------- Main render --------
  const authUser = auth.currentUser;

  // Values for tiles: prefer aggregate hook; fall back to local slice
  const totalCardsDisplay = statsAgg?.total ?? totalCards;
  const saleCountDisplay = statsAgg?.sale ?? forSale;
  const tradeCountDisplay = statsAgg?.trade ?? forTrade;
  const saleValueDisplay = statsAgg?.saleValue ?? totalValue;

  return (
    <div className="min-h-screen p-6 pb-[env(safe-area-inset-bottom)]">
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Indiegogo CTA (toggleable) with CashApp image to the left */}
        {SHOW_IGG_CTA && (
          <div className="flex items-start md:items-center gap-4">
            {/* CashApp image outside the SectionShell, aligned with the section */}
            <div className="shrink-0">
              <a
                  href="https://cash.app/$spellgather"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="shrink-0 inline-block"
                >
              <img
                src="/images/sg-cashapp.png"
                alt="Support via Cash App"
                className="h-64 w-auto rounded-md border border-white/0 object-contain"
              />
              </a>
            </div>

            <SectionShell
              title="Special Announcement"
              icon={<Newspaper className="w-5 h-5 text-sky-400" />}
            >
              <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
                <a
                  href="https://www.indiegogo.com/projects/spellgather-launch-funding#/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="shrink-0 inline-block"
                >
                  <img
                    src="/images/bob_broke.png"
                    alt="Support Spellgather on Indiegogo"
                    className="h-16 w-auto rounded-md border border-white/10 hover:opacity-90 transition"
                  />
                </a>
                <p className="text-slate-300 text-sm">
                  Checkout our Indiegogo campaign now live and considering supporting! All user's that
                  support our campaign will receive a special gift (if the campaign reaches its goal)
                  while supplies last.
                </p>
              </div>
            </SectionShell>
          </div>
        )}

        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4" data-tour="dashboard-header">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">
              Welcome back, <span className="text-fuchsia-400">{me?.display_name || me?.username || "Planeswalker"}</span>
            </h1>
            <p className="text-slate-400">
              Ready to expand your collection and connect with fellow players?
            </p>
          </div>
          <div className="rounded-xl border border-slate-700/50 bg-black/30 p-4 flex items-center gap-4">
            <RankBadge
              title={me?.rank_title || "Initiate"}
              points={
                typeof me?.rank_points_total === "number"
                  ? me.rank_points_total
                  : typeof me?.total_score === "number"
                  ? me.total_score
                  : 0
              }
              size="large"
              showPoints={false}
            />
            <div className="h-8 w-px bg-slate-700/60" />
            <div>
              <div className="text-xs text-slate-400">Points</div>
              <div className="text-2xl font-bold text-white">
                {Math.round(
                  typeof me?.rank_points_total === "number"
                    ? me.rank_points_total
                    : typeof me?.total_score === "number"
                    ? me.total_score
                    : 0
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-tour="dashboard-stats">
          <StatTile label="Total Cards" value={totalCardsDisplay} />
          <StatTile label="Listed for Trade" value={tradeCountDisplay} />
          <StatTile label="Listed for Sale" value={saleCountDisplay} />
          <StatTile
            label="Sale Value (sum)"
            value={`$${saleValueDisplay.toFixed(2)}`}
          />
        </div>

        {/* ALERTS — horizontal row */}
        <div data-tour="dashboard-alerts">
          <SectionShell
            title="We got what you need! (well someone on here does...)"
            icon={<Zap className="w-5 h-5 text-sky-400" />}
          >
            {alertsPending && alerts.length === 0 ? (
              <div className="text-slate-300 text-sm">
                Scanning marketplace for matches…
              </div>
            ) : alerts.length === 0 ? (
              <div className="text-slate-300 text-sm">
                No matches yet. Add cards to your Wantlist to see alerts here.
              </div>
            ) : (
              <div className="rounded-lg border border-sky-700/40 bg-sky-950/10">
                <div className="overflow-x-auto scrollbar-thin scrollbar-thumb-sky-700/50 scrollbar-track-transparent">
                  <div className="flex gap-3 p-2">
                    {alerts.map((it) => {
                      const key = `${it.ownerUid}:${it.id}`;
                      const profileStub = {
                        uid: it.ownerUid,
                        username: it.username,
                        display_name: it.ownerName,
                        avatar_url: it.ownerAvatar,
                      };
                      const listingStub = {
                        id: cardIdFromPath(it.id) ?? it.id,
                        name: it.name,
                        image: it.image ?? null,
                        status: it.for_sale ? "sale" : "trade",
                        price: it.price ?? null,
                        foil: !!it.foil,
                        set: it.set ?? null,
                        number: it.number ?? null,
                        scryfall_id: it.scryfall_id ?? null,
                      };

                      return (
                        <div key={key} style={{ width: 220 }}>
                          <ListingProfileCard
                            profile={profileStub}
                            listing={listingStub}
                            distanceMiles={it.distanceMi}
                            onOpenZoom={(l) => {
                              // normalize into ActivityCard-like zoom item
                              setZoom({
                                id: it.id,
                                ownerUid: it.ownerUid,
                                ownerName: it.ownerName,
                                username: it.username,
                                ownerAvatar: it.ownerAvatar,
                                name: l.name ?? it.name,
                                image: l.image ?? it.image,
                                status: listingStub.status as any,
                                set: listingStub.set,
                                number: listingStub.number,
                                foil: listingStub.foil,
                                price: listingStub.price,
                                scryfall_id: listingStub.scryfall_id,
                                createdAt: undefined,
                                for_sale: !!it.for_sale,
                                for_trade: !!it.for_trade,
                              });
                            }}
                            onOpenProfile={(unameOrUid) => {
                              // ListingProfileCard passes username; viewProfile expects username
                              if (unameOrUid) viewProfile(String(unameOrUid));
                            }}
                            onOpenOfferChain={(l) => {
                              openOffer({
                                id: it.id,
                                ownerUid: it.ownerUid,
                                ownerName: it.ownerName,
                                username: it.username,
                                ownerAvatar: it.ownerAvatar,
                                name: l.name ?? it.name,
                                image: l.image ?? it.image,
                                status: listingStub.status as any,
                                set: listingStub.set,
                                number: listingStub.number,
                                foil: listingStub.foil,
                                price: listingStub.price,
                                scryfall_id: listingStub.scryfall_id,
                                createdAt: undefined,
                                for_sale: !!it.for_sale,
                                for_trade: !!it.for_trade,
                              });
                            }}
                            className="w-full"
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </SectionShell>
        </div>

        {/* What's New — from Scoreboard */}
        <SectionShell
          title="What’s New"
          icon={<Newspaper className="w-5 h-5 text-sky-400" />}
        >
          {newsErr ? (
            <div className="text-slate-300 text-sm">{newsErr}</div>
          ) : newsItems.length === 0 ? (
            <div className="text-slate-300 text-sm">Nothing new.</div>
          ) : (
            <ul className="space-y-2">
              {newsItems.slice(0, 5).map((t, i) => (
                <li
                  key={`news-${i}`}
                  className="rounded-lg border border-slate-700/50 bg-black/30 p-3 text-white text-sm"
                >
                  {t}
                </li>
              ))}
            </ul>
          )}
          {newsUpdatedAt ? (
            <div className="mt-2 text-[11px] text-slate-400">
              Updated {new Date(newsUpdatedAt).toLocaleString()}
            </div>
          ) : null}
        </SectionShell>

        {/* Main Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Left (2 cols) */}
          <div className="md:col-span-2 space-y-6">
            {/* Followed users’ activity */}
            <div data-tour="dashboard-followed">
              <SectionShell
                title="Recent Activity — People you follow"
                icon={<Sparkles className="w-5 h-5 text-purple-400" />}
              >
                {followedActivity.length ? (
                  <>
                    <div className="flex items-center justify-end mb-2">
                      {followedActivity.length > COLLAPSE_COUNT && (
                        <button
                          type="button"
                          onClick={() => setExpandFollowed((v) => !v)}
                          className="text-xs rounded border border-white/10 px-2 py-1 text-slate-200 hover:bg-white/5"
                        >
                          {expandFollowed ? "Show less" : "Show all"}
                        </button>
                      )}
                    </div>

                    {/* Collapsed (first 5) */}
                    <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
                      {followedActivity.slice(0, COLLAPSE_COUNT).map((it) => {
                        const key = `${it.ownerUid}:${it.id}`;
                        const profileStub = {
                          uid: it.ownerUid,
                          username: it.username,
                          display_name: it.ownerName,
                          avatar_url: it.ownerAvatar,
                        };
                        const listingStub = {
                          id: cardIdFromPath(it.id) ?? it.id,
                          name: it.name,
                          image: it.image ?? null,
                          status: it.for_sale ? "sale" : "trade",
                          price: it.price ?? null,
                          foil: !!it.foil,
                          set: it.set ?? null,
                          number: it.number ?? null,
                          scryfall_id: it.scryfall_id ?? null,
                        };
                        return (
                          <div key={key}>
                            <ListingProfileCard
                              profile={profileStub}
                              listing={listingStub}
                              distanceMiles={it.distanceMi}
                              onOpenZoom={(l) => {
                                setZoom({
                                  id: it.id,
                                  ownerUid: it.ownerUid,
                                  ownerName: it.ownerName,
                                  username: it.username,
                                  ownerAvatar: it.ownerAvatar,
                                  name: l.name ?? it.name,
                                  image: l.image ?? it.image,
                                  status: listingStub.status as any,
                                  set: listingStub.set,
                                  number: listingStub.number,
                                  foil: listingStub.foil,
                                  price: listingStub.price,
                                  scryfall_id: listingStub.scryfall_id,
                                  createdAt: undefined,
                                  for_sale: !!it.for_sale,
                                  for_trade: !!it.for_trade,
                                });
                              }}
                              onOpenProfile={(unameOrUid) => {
                                if (unameOrUid) viewProfile(String(unameOrUid));
                              }}
                              onOpenOfferChain={(l) => {
                                openOffer({
                                  id: it.id,
                                  ownerUid: it.ownerUid,
                                  ownerName: it.ownerName,
                                  username: it.username,
                                  ownerAvatar: it.ownerAvatar,
                                  name: l.name ?? it.name,
                                  image: l.image ?? it.image,
                                  status: listingStub.status as any,
                                  set: listingStub.set,
                                  number: listingStub.number,
                                  foil: listingStub.foil,
                                  price: listingStub.price,
                                  scryfall_id: listingStub.scryfall_id,
                                  createdAt: undefined,
                                  for_sale: !!it.for_sale,
                                  for_trade: !!it.for_trade,
                                });
                              }}
                              className="w-full"
                            />
                          </div>
                        );
                      })}
                    </div>

                    {/* Expanded scrollable (all) */}
                    {expandFollowed && (
                      <div
                        className="mt-3 overflow-y-auto pr-1"
                        style={{ maxHeight: 380 }}
                        role="region"
                        aria-label="Followed activity — all"
                      >
                        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
                          {followedActivity.map((it) => {
                            const key = `${it.ownerUid}:${it.id}`;
                            const profileStub = {
                              uid: it.ownerUid,
                              username: it.username,
                              display_name: it.ownerName,
                              avatar_url: it.ownerAvatar,
                            };
                            const listingStub = {
                              id: cardIdFromPath(it.id) ?? it.id,
                              name: it.name,
                              image: it.image ?? null,
                              status: it.for_sale ? "sale" : "trade",
                              price: it.price ?? null,
                              foil: !!it.foil,
                              set: it.set ?? null,
                              number: it.number ?? null,
                              scryfall_id: it.scryfall_id ?? null,
                            };
                            return (
                              <div key={key}>
                                <ListingProfileCard
                                  profile={profileStub}
                                  listing={listingStub}
                                  distanceMiles={it.distanceMi}
                                  onOpenZoom={(l) => {
                                    setZoom({
                                      id: it.id,
                                      ownerUid: it.ownerUid,
                                      ownerName: it.ownerName,
                                      username: it.username,
                                      ownerAvatar: it.ownerAvatar,
                                      name: l.name ?? it.name,
                                      image: l.image ?? it.image,
                                      status: listingStub.status as any,
                                      set: listingStub.set,
                                      number: listingStub.number,
                                      foil: listingStub.foil,
                                      price: listingStub.price,
                                      scryfall_id: listingStub.scryfall_id,
                                      createdAt: undefined,
                                      for_sale: !!it.for_sale,
                                      for_trade: !!it.for_trade,
                                    });
                                  }}
                                  onOpenProfile={(unameOrUid) => {
                                    if (unameOrUid) viewProfile(String(unameOrUid));
                                  }}
                                  onOpenOfferChain={(l) => {
                                    openOffer({
                                      id: it.id,
                                      ownerUid: it.ownerUid,
                                      ownerName: it.ownerName,
                                      username: it.username,
                                      ownerAvatar: it.ownerAvatar,
                                      name: l.name ?? it.name,
                                      image: l.image ?? it.image,
                                      status: listingStub.status as any,
                                      set: listingStub.set,
                                      number: listingStub.number,
                                      foil: listingStub.foil,
                                      price: listingStub.price,
                                      scryfall_id: listingStub.scryfall_id,
                                      createdAt: undefined,
                                      for_sale: !!it.for_sale,
                                      for_trade: !!it.for_trade,
                                    });
                                  }}
                                  className="w-full"
                                />
                              </div>
                            );
                          })}
                        </div>
                       </div>
                     )}
                   </>
                 ) : (
                   <div className="text-slate-300 text-sm">
                     No recent activity from people you follow.
                   </div>
                 )}
               </SectionShell>
             </div>
 
             {/* Nearby trade activity */}
             <div data-tour="dashboard-nearby">
               <SectionShell
                 title="Recent Activity — Within your trade radius"
                 icon={<MapPin className="w-5 h-5 text-emerald-400" />}
               >
                 {nearbyTrade.length ? (
                   <>
                     <div className="flex items-center justify-end mb-2">
                       {nearbyTrade.length > COLLAPSE_COUNT && (
                         <button
                           type="button"
                           onClick={() => setExpandNearby((v) => !v)}
                           className="text-xs rounded border border-white/10 px-2 py-1 text-slate-200 hover:bg-white/5"
                         >
                           {expandNearby ? "Show less" : "Show all"}
                         </button>
                       )}
                     </div>
 
                     {/* Collapsed (first 5) */}
                     <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
                       {nearbyTrade.slice(0, COLLAPSE_COUNT).map((it) => {
                         const key = `${it.ownerUid}:${it.id}`;
                         const profileStub = {
                           uid: it.ownerUid,
                           username: it.username,
                           display_name: it.ownerName,
                           avatar_url: it.ownerAvatar,
                         };
                         const listingStub = {
                           id: cardIdFromPath(it.id) ?? it.id,
                           name: it.name,
                           image: it.image ?? null,
                           status: it.for_sale ? "sale" : "trade",
                           price: it.price ?? null,
                           foil: !!it.foil,
                           set: it.set ?? null,
                           number: it.number ?? null,
                           scryfall_id: it.scryfall_id ?? null,
                         };
                         return (
                           <div key={key}>
                             <ListingProfileCard
                               profile={profileStub}
                               listing={listingStub}
                               distanceMiles={it.distanceMi}
                               onOpenZoom={(l) => {
                                 setZoom({
                                   id: it.id,
                                   ownerUid: it.ownerUid,
                                   ownerName: it.ownerName,
                                   username: it.username,
                                   ownerAvatar: it.ownerAvatar,
                                   name: l.name ?? it.name,
                                   image: l.image ?? it.image,
                                   status: listingStub.status as any,
                                   set: listingStub.set,
                                   number: listingStub.number,
                                   foil: listingStub.foil,
                                   price: listingStub.price,
                                   scryfall_id: listingStub.scryfall_id,
                                   createdAt: undefined,
                                   for_sale: !!it.for_sale,
                                   for_trade: !!it.for_trade,
                                 });
                               }}
                               onOpenProfile={(unameOrUid) => {
                                 if (unameOrUid) viewProfile(String(unameOrUid));
                               }}
                               onOpenOfferChain={(l) => {
                                 openOffer({
                                   id: it.id,
                                   ownerUid: it.ownerUid,
                                   ownerName: it.ownerName,
                                   username: it.username,
                                   ownerAvatar: it.ownerAvatar,
                                   name: l.name ?? it.name,
                                   image: l.image ?? it.image,
                                   status: listingStub.status as any,
                                   set: listingStub.set,
                                   number: listingStub.number,
                                   foil: listingStub.foil,
                                   price: listingStub.price,
                                   scryfall_id: listingStub.scryfall_id,
                                   createdAt: undefined,
                                   for_sale: !!it.for_sale,
                                   for_trade: !!it.for_trade,
                                 });
                               }}
                               className="w-full"
                             />
                           </div>
                         );
                       })}
                     </div>
 
                     {/* Expanded scrollable (all) */}
                     {expandNearby && (
                       <div
                         className="mt-3 overflow-y-auto pr-1"
                         style={{ maxHeight: 380 }}
                         role="region"
                         aria-label="Nearby trade activity — all"
                       >
                         <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
                           {nearbyTrade.map((it) => {
                             const key = `${it.ownerUid}:${it.id}`;
                             const profileStub = {
                               uid: it.ownerUid,
                                                             username: it.username,
                               display_name: it.ownerName,
                               avatar_url: it.ownerAvatar,
                             };
                             const listingStub = {
                               id: cardIdFromPath(it.id) ?? it.id,
                               name: it.name,
                               image: it.image ?? null,
                               status: it.for_sale ? "sale" : "trade",
                               price: it.price ?? null,
                               foil: !!it.foil,
                               set: it.set ?? null,
                               number: it.number ?? null,
                               scryfall_id: it.scryfall_id ?? null,
                             };
                             return (
                               <div key={key}>
                                 <ListingProfileCard
                                   profile={profileStub}
                                   listing={listingStub}
                                   distanceMiles={it.distanceMi}
                                   onOpenZoom={(l) => {
                                     setZoom({
                                       id: it.id,
                                       ownerUid: it.ownerUid,
                                       ownerName: it.ownerName,
                                       username: it.username,
                                       ownerAvatar: it.ownerAvatar,
                                       name: l.name ?? it.name,
                                       image: l.image ?? it.image,
                                       status: listingStub.status as any,
                                       set: listingStub.set,
                                       number: listingStub.number,
                                       foil: listingStub.foil,
                                       price: listingStub.price,
                                       scryfall_id: listingStub.scryfall_id,
                                       createdAt: undefined,
                                       for_sale: !!it.for_sale,
                                       for_trade: !!it.for_trade,
                                     });
                                   }}
                                   onOpenProfile={(unameOrUid) => {
                                     if (unameOrUid) viewProfile(String(unameOrUid));
                                   }}
                                   onOpenOfferChain={(l) => {
                                     openOffer({
                                       id: it.id,
                                       ownerUid: it.ownerUid,
                                       ownerName: it.ownerName,
                                       username: it.username,
                                       ownerAvatar: it.ownerAvatar,
                                       name: l.name ?? it.name,
                                       image: l.image ?? it.image,
                                       status: listingStub.status as any,
                                       set: listingStub.set,
                                       number: listingStub.number,
                                       foil: listingStub.foil,
                                       price: listingStub.price,
                                       scryfall_id: listingStub.scryfall_id,
                                       createdAt: undefined,
                                       for_sale: !!it.for_sale,
                                       for_trade: !!it.for_trade,
                                     });
                                   }}
                                   className="w-full"
                                 />
                               </div>
                             );
                           })}
                         </div>
                       </div>
                     )}
                   </>
                 ) : (
                   <div className="text-slate-300 text-sm">
                     No nearby trade activity yet.
                   </div>
                 )}
               </SectionShell>
             </div>
          </div>

          {/* Right (1 col) */}
          <div className="space-y-6">
            <div data-tour="dashboard-highlights">
              <SectionShell
                title="Collection Highlights"
                icon={<Star className="w-5 h-5 text-yellow-400" />}
              >
                <div className="space-y-3">
                  <Row label="Most Valuable" value={mostValuable || "--"} />
                  <Row label="Newest Addition" value={newest || "--"} />
                  <Row label="Rarest Card" value={rarest || "--"} />
                </div>
              </SectionShell>
            </div>

            {/* New mini heatmap above nearby players (client-only; wrapped in ErrorBoundary) */}
            <ErrorBoundary>
              <div data-tour="heatmap-widget">
                {mounted ? (
                  <HeatmapWidget />
                ) : (
                  <div
                    className="flex items-center justify-center h-32 rounded-lg border border-slate-700/50 bg-black/30 text-xs text-slate-400"
                  >
                    Loading map…
                  </div>
                )}
              </div>
            </ErrorBoundary>

            {/* Nearby players */}
            <SectionShell
              title="Nearby Players"
              icon={<Users className="w-5 h-5 text-sky-400" />}
            >
              <div className="space-y-3">
                {uniqueNearbyPlayers.slice(0, 6).map((c) => {
                  const key = `${c.ownerUid}:nearby`;
                  const isOpen = !!menu && menu.key === key;
                  return (
                    <div
                      key={key}
                      className="rounded-lg p-2 bg-black/30 border border-slate-700/50"
                    >
                      <div className="flex items-center gap-3">
                        <button
                          className="shrink-0 rounded-full border border-slate-700 overflow-hidden"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleMenuListing(
                              c.ownerUid,
                              c.username || c.ownerName,
                              "nearby",
                              e.currentTarget as HTMLElement,
                            );
                          }}
                          aria-label={`@${c.username || c.ownerName}`}
                          title={`@${c.username || c.ownerName}`}
                        >
                          <img
                            src={c.ownerAvatar || "/images/avatars/default.png"}
                            alt={c.username || c.ownerName}
                            className="w-8 h-8 object-cover"
                          />
                        </button>
                        <button
                          className="flex-1 min-w-0 text-left"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleMenuListing(
                              c.ownerUid,
                              c.username || c.ownerName,
                              "nearby",
                              e.currentTarget as HTMLElement, // anchor for portal positioning
                            );
                          }}
                          title={`@${c.username || c.ownerName}`}
                        >
                          <div className="text-white text-sm truncate">
                            @{c.username || c.ownerName}
                          </div>
                          <div className="text-xs text-slate-400">
                            {c.distanceMi?.toFixed(1) ?? "--"} mi
                          </div>
                        </button>
                      </div>

                      {isOpen && <div className="hidden" aria-hidden="true" />}
                    </div> ); })}
                {!uniqueNearbyPlayers.length && (
                  <div className="text-slate-300 text-sm">
                    No nearby players yet.
                  </div>
                )}
              </div>
            </SectionShell>

            {/* Nearby events */}
            <SectionShell
              title="Upcoming Events — Nearby"
              icon={<MapPin className="w-5 h-5 text-emerald-400" />}
            >
              <div className="flex items-center gap-2 mb-2">
                <div className="text-slate-300 text-sm">Closest/Soonest</div>
                <div className="ml-auto text-xs text-slate-400">
                  {eventsNearby.length ? `${eventsIndex + 1} / ${eventsNearby.length}` : "No events"}
                </div>
              </div>
              <div className="relative w-full h-48">
                {/* Left chevron */}
                <button
                  type="button"
                  onClick={() => setEventsIndex(i => (eventsNearby.length ? (i - 1 + eventsNearby.length) % eventsNearby.length : 0))}
                  aria-label="Previous event"
                  className="absolute left-1 top-1/2 -translate-y-1/2 z-20 rounded-full bg-black/60 p-2 border border-white/10 hover:bg-white/5"
                >
                  <ChevronLeft className="w-4 h-4 text-white" />
                </button>

                {/* Stacked mini cards: show prev/current/next for visual depth */}
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  {(() => {
                    if (!eventsNearby.length) {
                      return <div className="text-slate-300">No nearby public events.</div>;
                    }
                    const idx = eventsIndex;
                    const cur = eventsNearby[idx];
                    const mini = (ev: any) => {
                      const baseCls = "pointer-events-auto relative w-[320px] max-w-[88%] h-40 rounded-lg overflow-hidden border shadow-md";
                      const posStyle: any = { transform: "translateX(0) translateY(0) scale(1)", zIndex: 30 };
                      return (
                        <button
                          key={ev?.id}
                          onClick={(e) => {
                            e.stopPropagation();
                            const href = `/${encodeURIComponent(ev.organizerUsername || "")}?tab=events&focus=${encodeURIComponent(ev.id)}`;
                            navigate(href);
                          }}
                          className={baseCls + " bg-slate-900/80 border-white/10"}
                          style={{ ...posStyle, transition: "transform 220ms ease, opacity 220ms" }}
                          title={ev.title}
                        >
                          <div className="flex h-full">
                            <div className="w-1/3 bg-black/40 flex items-center justify-center">
                              <img
                                src={ev.images && ev.images.length ? ev.images[0] : "/events/placeholder.png"}
                                alt={ev.title}
                                className="h-full w-full object-cover"
                                onError={(e:any) => { e.currentTarget.onerror = null; e.currentTarget.src="/events/placeholder.png"; }}
                              />
                            </div>
                            <div className="flex-1 p-2 text-left text-sm text-slate-100">
                              <div className="font-semibold truncate">{ev.title}</div>
                              <div className="text-xs text-slate-400 mt-1 truncate">
                                {ev.organizerDisplayName || `@${ev.organizerUsername || "organizer"}`}
                              </div>
                              <div className="text-xs text-slate-400 mt-2">
                                {fmtShortDate(ev.startTime)}
                              </div>
                              <div className="text-xs text-slate-400 mt-1">
                                {ev.distanceMi != null ? `${ev.distanceMi.toFixed(1)} mi` : "—"}
                              </div>
                            </div>
                          </div>
                        </button>
                      );
                    };

                    return (
                      <div className="relative w-full flex items-center justify-center" style={{ height: 160 }}>
                        <div style={{ position: "absolute", left: "50%", transform: "translateX(-50%)" }}>
                          {cur ? mini(cur) : null}
                        </div>
                      </div>
                    );
                   })()}
                </div>
                {/* Right chevron */}
                <button
                  type="button"
                  onClick={() => setEventsIndex(i => (eventsNearby.length ? (i + 1) % eventsNearby.length : 0))}
                  aria-label="Next event"
                  className="absolute right-1 top-1/2 -translate-y-1/2 z-20 rounded-full bg-black/60 p-2 border border-white/10 hover:bg-white/5"
                >
                  <ChevronRight className="w-4 h-4 text-white" />
                </button>
              </div>
            </SectionShell>

            {/* Play opportunities */}
            <div data-tour="dashboard-playopps">
              <SectionShell
                title="Play opportunities — Similar formats"
                icon={<Users className="w-5 h-5 text-sky-400" />}
              >
                {playOpps.length ? (
                  <div className="space-y-2">
                    {playOpps.map((p) => (
                      <Link
                        to={`/${p.username}`}
                        key={p.uid}
                        className="flex items-center gap-3 hover:bg-white/5 rounded-lg p-2"
                      >
                        <img
                          src={p.avatar_url || "/images/avatars/default.png"}
                          alt={p.username}
                          className="w-9 h-9 rounded-full object-cover border border-slate-600"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-white text-sm truncate">
                            @{p.username}
                          </div>
                          <div className="text-xs text-slate-400">
                            {p.formats
                              ?.slice(0, 3)
                              .map((f) => FORMAT_LABELS[f] ?? f)
                              .join(", ") || "Formats match"}
                            {p.distanceMi != null
                              ? ` • ${p.distanceMi.toFixed(1)} mi`
                              : ""}
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <div className="text-slate-300 text-sm">
                    No new play opportunities right now.
                  </div>
                )}
              </SectionShell>
            </div>
          </div>
        </div>
      </div>

      {/* Card Oracle/Details modal (click any card image) */}
      <CardDetailsModal
        open={!!zoom}
        onClose={() => setZoom(null)}
        item={{
          name: zoom?.name || "",
          image: zoom?.image || null,
          set: zoom?.set ?? null,
          number: zoom?.number ?? null,
          foil: Boolean(zoom?.foil),
          status: zoom?.for_sale ? "sale" : "trade",
          price: zoom?.price ?? null,
          scryfall_id: zoom?.scryfall_id || null,
          ownerUid: zoom?.ownerUid || "",
          username: zoom?.username || zoom?.ownerName || "",
          ownerAvatar: zoom?.ownerAvatar || null,
          distance: zoom?.distanceMi,
          docId: cardIdFromPath(zoom?.id || "") ?? null,
        }}
        onMakeOffer={openOfferFromZoom}
      />

      {/* Offer Modal (same UX as Marketplace) */}
      {offerOpen && offerCard && (
        <div className="fixed inset-0 z-[95]" role="dialog" aria-modal="true" aria-labelledby="offer-title">
          <div className="absolute inset-0 bg-black/70" aria-hidden onClick={() => setOfferOpen(false)} />
          <div className="absolute inset-0 grid place-items-center md:p-4">
            <div className="relative w-full h-[100dvh] md:h-auto md:w-[min(560px,95vw)] overflow-hidden md:rounded-xl rounded-none border border-slate-700 bg-slate-900 text-slate-100 shadow-2xl flex flex-col pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]">
              <div className="sticky top-0 z-10 flex items-center justify-between px-4 py-3 border-b border-slate-800 bg-slate-900/95 backdrop-blur supports-[backdrop-filter]:bg-slate-900/60">
                <h3 id="offer-title" className="text-lg font-semibold">
                  {offerCard.for_sale ? "Make an Offer" : "Propose a Trade"}
                </h3>
                <button onClick={() => setOfferOpen(false)} className="text-slate-300 hover:text-white" aria-label="Close">✕</button>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                <div className="flex gap-3">
                  <img
                    src={offerCard.image || "/images/cardback.png"}
                    className="h-24 w-18 object-cover rounded border border-slate-700"
                    alt={offerCard.name}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{offerCard.name}</div>
                    <div className="text-xs opacity-80 truncate">
                      {(offerCard.set || "")} {offerCard.number ? `• #${offerCard.number}` : ""} {offerCard.foil ? "• Foil" : ""}
                    </div>
                    <div className="text-sm mt-1">
                      {offerCard.for_sale ? (
                        <span className="font-semibold">
                          {offerCard.price != null ? `$${Number(offerCard.price).toFixed(2)}` : "—"}
                        </span>
                      ) : (
                        <span className="font-semibold text-cyan-300">For Trade</span>
                      )}
                    </div>
                  </div>
                </div>
                {offerCard.for_sale && (
                  <div className="grid gap-2">
                    <label htmlFor="offer-price" className="text-sm">Your offer (USD, optional)</label>
                    <input
                      id="offer-price"
                      type="number"
                      min="0"
                      step="0.01"
                      inputMode="decimal"
                      className="border rounded px-3 py-2 text-black"
                      placeholder={offerCard.price ? `e.g. ${Number(offerCard.price).toFixed(2)}` : "Enter an amount"}
                      value={offerPrice}
                      onChange={(e) => setOfferPrice(e.target.value)}
                    />
                  </div>
                )}
                <div className="grid gap-2">
                  <label htmlFor="offer-message" className="text-sm">Message</label>
                  <textarea
                    id="offer-message"
                    rows={5}
                    className="border rounded px-3 py-2 text-black"
                    placeholder={offerCard.for_sale ? "Hi! I'm interested in buying this card…" : "Hi! I'm interested in trading for this card. I can offer…"}
                    value={offerMessage}
                    onChange={(e) => setOfferMessage(e.target.value)}
                  />
                </div>
              </div>
              <div className="sticky bottom-0 z-10 flex items-center justify-end gap-2 px-4 py-3 border-t border-slate-800 bg-slate-900/95 backdrop-blur supports-[backdrop-filter]:bg-slate-900/60">
                <Button variant="outline" onClick={() => setOfferOpen(false)}>Cancel</Button>
                <Button onClick={submitOffer} disabled={submittingOffer}>
                  {submittingOffer ? "Sending…" : offerCard.for_sale ? "Send Offer" : "Send Trade Proposal"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Location confirm gate */}
      <LocationConfirmGate
        open={locationModalOpen}
        autoOnLogin={autoLocationOnLogin}
        onToggleAuto={toggleAutoLocation}
        busy={locBusy}
        onUseCurrent={applyCurrentLocation}
        onKeepSaved={() => setLocationModalOpen(false)}
      />

      {/* Missing location nag modal */}
      {missingLocNagOpen && (
        <div className="fixed inset-0 z-[60]" role="dialog" aria-modal="true">
          <div className="absolute inset-0 bg-black/70" onClick={() => setMissingLocNagOpen(false)} />
          <div className="absolute inset-0 grid place-items-center p-4">
            <div className="w-[min(92vw,520px)] rounded-2xl border border-sky-800 bg-[#0c1222] text-white shadow-2xl">
              <div className="p-5 border-b border-sky-800/50">
                <h3 className="text-lg font-bold">Enable location to unlock the full experience</h3>
              </div>
              <div className="p-5 space-y-3 text-slate-200">
                <p>
                  Setting your approximate location helps show nearby players, local listings, store
                  recommendations, and play opportunities. Coordinates are stored in an obfuscated form and
                  used only to power these features.
                </p>
                <p className="text-slate-300">
                  You can turn off location-based features anytime by toggling “Trade” and/or “Play” in the avatar menu.
                </p>
              </div>
              <div className="p-5 pt-0 flex flex-wrap gap-2 justify-end">
                <button

                  className="btn-pixel btn-pixel-secondary"
                  onClick={() => {
                    setMissingLocNagOpen(false);
                  }}
                >
                  Not now
                </button>
                <button
                  className="btn-pixel btn-pixel-primary"
                  onClick={() => {
                    setMissingLocNagOpen(false);
                    setLocationModalOpen(true); // reuse existing gate
                  }}
                >
                  Use my location
                </button>
                <Link to="/settings" className="btn-pixel btn-pixel-outline">
                  Open Settings
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Floating ProfileInlineMenu for LISTING menus (portal) */}
      {menu && listingMenuBox &&
        createPortal(
          <div className="fixed z-[2147483647]" style={{ top: listingMenuBox.top, left: listingMenuBox.left, width: 320, maxWidth: "95vw" }} data-market-interactive="1">
            <div className="rounded-lg border border-amber-400/40 bg-black/90 backdrop-blur-sm shadow-xl shadow-amber-400/10 overflow-hidden">
              <ProfileInlineMenu
               
                key={menu.key}
                targetUid={menu.ownerUid}
                username={menu.username}
                onMessage={() => navigate(`/inbox?to=${encodeURIComponent(menu.ownerUid)}`)}
                onViewProfile={() => viewProfile(menu.username)}
                onMakeOffer={() => {
                  // Mirror Marketplace: open Card Details first; user clicks Make offer to open Offer modal
                  setMenu(null);
                  openCardDetailsByMenu();
                }}
                onClose={() => setMenu(null)}
                className="w-full max-w-[calc(100vw-32px)]"
              />
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}

